import memoize from 'lodash/memoize'

import { appConfig } from 'utils/app-config'
import { isProductionEnvironment } from 'utils/environment'

// Allow environment variable overrides for API bases when running Gremlin services locally
const { REACT_APP_API_URL, SUPPORT_APP_API_BASE } = process.env
const STANDARD_HOSTS = ['app.gremlin.com', 'app.staging.gremlin.com']
// Pattern for a new staging host is
const STAGING_PATTERN = /^app\.[^.]*\.staging\.gremlin\.com$/
const SUBDOMAINS = { api: 'api', support: 'api.support' }

export class BaseUrlBuilder {
  key: string
  location: Location | URL

  constructor(location: Location | URL, key: string) {
    if (!(key in this.envUrls)) {
      throw new Error(`unknown host key ${key}`)
    }

    this.key = key
    this.location = location
  }

  // All supported keys must have an env key translation here
  get envUrls() {
    const { API_URL, SUPPORT_APP_API_BASE: supportAppApiBase } =
      appConfig.getState()

    // Use config values exclusively in production
    if (isProductionEnvironment())
      return { api: API_URL, support: supportAppApiBase }

    // When developing locally, allow for environment variable overrides if provided
    // Otherwise, fall back to the config values
    return {
      api: REACT_APP_API_URL ?? API_URL,
      support: SUPPORT_APP_API_BASE ?? supportAppApiBase
    }
  }

  // Cases where we are allowed to compute the url:
  //  app.gremlin.com
  //  app.staging.gremlin.com
  validateComputable() {
    const { host } = this.location

    if (
      !window.Cypress &&
      !STANDARD_HOSTS.includes(host) &&
      !STAGING_PATTERN.test(host)
    )
      throw new Error(`Cannot compute base host for ${host}`)
  }

  build() {
    // Override based on environment if provided
    if (this.envUrls[this.key]) return this.envUrls[this.key]

    // Ensure that we are able to compute the host for this URL.
    this.validateComputable()

    // Compute based on the location
    const { protocol, host } = this.location

    // Chop off "app." to get the base host
    const hostBase = host.slice('app.'.length)
    return `${protocol}//${SUBDOMAINS[this.key]}.${hostBase}`
  }
}

// Main function
export const getBaseUrl = memoize((key) =>
  new BaseUrlBuilder(window.location, key).build()
)
